import { type TypedDay, parseTypedDay } from "$root/components/date-picker-popover";
import {
	localDateToServerDate,
	serverTodayAsLocalDate,
	serverYesterdayAsLocalDate,
	utcDateToLocalDate,
} from "$root/utils";
import type { InvestmentSummary } from "@eu.mdotm.api/sphere";
import { DateTime } from "luxon";

/**
 * Date rules for the composition editor's market date (MDOT-25190).
 *
 * The editor loads **today's holdings priced at the market date** — it does not roll the
 * composition back to what the portfolio held that day. That is what separates this from the
 * portfolio history view's "as of", and why neither bound matches it: the window here runs from the
 * portfolio's last trade date up to and including server-today, which is the window
 * `POST /v4/investments/shares/prices/{investment-uuid}` enforces server-side.
 *
 * All dates are `YYYY-MM-DD` strings in the user's local calendar: `?marketDate` carries the local
 * day so a shared link means the same day wherever it is opened. {@link pastDatedEditDate} is the
 * one place that crosses into the server calendar, at the edge where a save is written.
 */

/** The selectable window, in the `YYYY-MM-DD` form the `Calendar` takes for `min` / `max`. */
export function marketDateBounds(opts: { lastTradeDate?: string | null } = {}): {
	min: string | undefined;
	max: string;
} {
	return {
		min: opts.lastTradeDate ? utcDateToLocalDate(opts.lastTradeDate) : undefined,
		max: serverTodayAsLocalDate(),
	};
}

/**
 * Validates a raw `?marketDate` URL param into a usable market date.
 *
 * Returns `null` (meaning: fall back to today, drop the param) when the value is unparsable,
 * precedes the portfolio's last trade date, or is after server-today. Today itself is accepted —
 * it is the default, and pricing today's holdings at today is what the editor does with no param at
 * all. Every day in range is selectable, weekends included.
 *
 * @param raw the raw query-param value
 * @param opts.lastTradeDate the portfolio's last trade date (a UTC timestamp or `YYYY-MM-DD`); when
 *        absent the lower-bound check is skipped, because the summary carrying it resolves after the
 *        first render and rejecting on its absence would delete a deep link before the data that
 *        accepts it arrives.
 */
export function parseMarketDateParam(
	raw: string | null | undefined,
	opts: { lastTradeDate?: string | null } = {},
): string | null {
	if (!raw) {
		return null;
	}
	const parsed = DateTime.fromISO(raw);
	if (!parsed.isValid || parsed.toISODate() !== raw) {
		return null;
	}
	const { min, max } = marketDateBounds(opts);
	if (min && raw < min) {
		return null;
	}
	if (raw > max) {
		return null;
	}
	return raw;
}

/**
 * Reads what the user typed into the market date field, in their locale's short date format.
 *
 * Settled by `parseMarketDateParam`, the same function that vets `?marketDate`: what can be
 * typed and what can be linked to are then one set by construction, rather than two that agree until
 * someone changes one of them.
 */
export function parseTypedMarketDate(
	typed: string,
	opts: { locale?: string; lastTradeDate?: string | null } = {},
): TypedDay {
	return parseTypedDay(typed, {
		locale: opts.locale,
		accept: (day) => parseMarketDateParam(day, { lastTradeDate: opts.lastTradeDate }),
	});
}

/**
 * Whether there is more than one selectable day — i.e. whether the control has anything to offer.
 *
 * The upper bound is today-inclusive, so when the last trade is today, today is the only day in the
 * window and it is already the default. The same holds for a portfolio that has never traded, which
 * carries no lower bound at all. The control still renders in both cases, disabled and reading
 * "Today": a greyed control says the date is normally changeable, where the absent one would say
 * nothing at all.
 */
export function isMarketDateSelectable(summary?: Pick<InvestmentSummary, "lastTradeDate"> | null): boolean {
	return Boolean(summary?.lastTradeDate && utcDateToLocalDate(summary.lastTradeDate) <= serverYesterdayAsLocalDate());
}

/**
 * The `editDate` a save carries for the day the header holds (MDOT-25192) — the local day in the
 * server's (Europe/Rome) calendar, which is the one the API dates the write at.
 *
 * `undefined` at today, and for a day that is absent: that is the request shape every save sent
 * before an effective date could be set at all, so a composition left at today reaches the backend
 * exactly as it always did.
 */
export function pastDatedEditDate(day: string | null | undefined): string | undefined {
	if (!day || day === serverTodayAsLocalDate()) {
		return undefined;
	}
	return localDateToServerDate(day);
}
